/**
 * The price range the shared `PriceRangeFilter` edits, and the arithmetic both of its platform
 * variants share: dollar stops, where an amount sits on the track, and how a range reads.
 */
/**
 * The dollar stops a thumb snaps to. Card prices are heavily skewed (most under $20, a few in the
 * thousands), so a linear track would cram nearly every card into its first sliver. An end thumb at
 * its end stop is an open bound: the first stop is "no minimum" and the last "no maximum".
 */
export const PRICE_STOPS = [0, 1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2500, 5000, 10000];
export const LAST_STOP = PRICE_STOPS.length - 1;

export type PriceRange = { min: string; max: string; };

/** Dollar text to a number, or null when blank or not a non-negative number. */
export function dollars(text: string): number | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * The bounds that actually constrain. A max that cannot match anything (zero, or at or below the
 * min, as while it is still being typed past the min) is left open, so the heading, the thumbs and
 * a server request (the inventory API refuses such a max with a 422) all read one valid range.
 */
export function rangeBounds({ min, max }: PriceRange): { lo: number | null; hi: number | null; } {
  const lo = dollars(min);
  const hi = dollars(max);
  return { lo, hi: hi != null && hi > 0 && (lo == null || hi > lo) ? hi : null };
}

/**
 * Where a range's two thumbs sit on the track, never crossed. The track ends at the last stop, so a
 * typed minimum at or past it (a $10,000+ card) pins one stop short of the end, beside the open max;
 * the heading and the fields still show the exact amounts.
 */
export function rangePositions(range: PriceRange): { lo: number; hi: number; } {
  const { lo, hi } = rangeBounds(range);
  return { lo: Math.min(positionOf(lo, "min"), LAST_STOP - 1), hi: positionOf(hi, "max") };
}

/**
 * The range after a thumb moves from `from` to `to`. The end that did not move keeps the amount the
 * customer typed, so dragging one thumb never rounds the other to a stop or clears a typed max
 * beyond the last stop.
 */
export function rangeAfterMove(
  value: PriceRange,
  from: { lo: number; hi: number; },
  to: { lo: number; hi: number; },
): PriceRange {
  return {
    min: to.lo === from.lo ? value.min : stopText(to.lo, "min"),
    max: to.hi === from.hi ? value.max : stopText(to.hi, "max"),
  };
}

/** A dollar amount's place on the track, between stops for a typed amount (0..LAST_STOP). */
export function positionOf(value: number | null, end: "min" | "max"): number {
  if (value == null) return end === "min" ? 0 : LAST_STOP;
  if (value >= PRICE_STOPS[LAST_STOP]) return LAST_STOP;
  const above = PRICE_STOPS.findIndex((s) => s > value);
  const lo = PRICE_STOPS[above - 1];
  return above - 1 + (value - lo) / (PRICE_STOPS[above] - lo);
}

export const money = (n: number) => `$${n.toLocaleString()}`;

/** The range as one phrase: "Any price", "$5 – $100", "$100+", "Up to $20". */
export function priceRangeLabel(range: PriceRange): string {
  const { lo, hi } = rangeBounds(range);
  if (lo != null && hi != null) return `${money(lo)} – ${money(hi)}`;
  if (lo != null) return `${money(lo)}+`;
  if (hi != null) return `Up to ${money(hi)}`;
  return "Any price";
}

/** What a thumb announces: its end's amount, or that the end is open (never "$NaN"). */
export function thumbText(range: PriceRange, end: "min" | "max"): string {
  const { lo, hi } = rangeBounds(range);
  const amount = end === "min" ? lo : hi;
  return amount != null ? money(amount) : end === "min" ? "No minimum" : "No maximum";
}

/** A stop's dollar text, blank at the open end. */
export function stopText(position: number, end: "min" | "max"): string {
  const i = Math.round(position);
  if ((end === "min" && i <= 0) || (end === "max" && i >= LAST_STOP)) return "";
  return String(PRICE_STOPS[i]);
}
